import getEnvValue from 'commons/getEnvValue';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import { getOrCreateBrowserAlgoliaUserToken } from 'commons/algoliaUserToken';
import { getAlgoliaAnalyticsParams } from 'commons/algoliaAnalyticsContext';

// Note: Using require here to allow conditional loading of different algoliasearch versions
// in the new and existing Consumer apps. After migration to the new app, we should change
// this to use a normal import.
const algoliasearchModule = require('algoliasearch');

// v5 exposes a named `algoliasearch` export, v4 only a default export.
const isAlgoliasearchV5 =
  typeof algoliasearchModule?.algoliasearch === 'function';

const algoliasearch = algoliasearchModule?.algoliasearch ?? algoliasearchModule;

// The default browser timeouts are too aggressive for slow mobile connections:
// every host in the retry chain times out and the client throws
// `RetryError: Unreachable hosts` even though Algolia is up. Note the unit
// differs between major versions — v5 takes milliseconds, v4 takes seconds.
const CLIENT_TIMEOUTS = isAlgoliasearchV5
  ? { connect: 2000, read: 5000, write: 30000 }
  : { connect: 2, read: 5, write: 30 };

// Lazy initialization - only create the client when actually needed
let algoliaClientInstance = null;

const getAlgoliaClient = () => {
  if (!algoliaClientInstance) {
    const appId =
      getEnvValue('NEXT_PUBLIC_ALGOLIA_APPID') ??
      getEnvValue('REACT_APP_ALGOLIA_APPID');
    const apiKey =
      getEnvValue('NEXT_PUBLIC_ALGOLIA_APIKEY') ??
      getEnvValue('REACT_APP_ALGOLIA_APIKEY');

    if (!appId || !apiKey) {
      // Environment variables not ready yet, client will be initialized when credentials are ready
      return null;
    }

    algoliaClientInstance = algoliasearch(appId, apiKey, {
      timeouts: CLIENT_TIMEOUTS,
    });
  }
  return algoliaClientInstance;
};

// Legacy export for backward compatibility (deprecated)
export const algoliaClient = {
  search: requests => {
    const client = getAlgoliaClient();
    if (!client) {
      // Algolia client not ready, returning empty results
      return Promise.resolve({
        results: requests.map(() => ({
          query: '',
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
        })),
      });
    }
    return client.search(requests);
  },
  initIndex: indexName => {
    const client = getAlgoliaClient();
    if (!client) {
      // Algolia client not ready, cannot initialize index
      // Return a mock index object that won't break the component
      return {
        search: () => Promise.resolve({ hits: [], nbHits: 0 }),
        browseObjects: () => Promise.resolve({ hits: [], nbHits: 0 }),
      };
    }
    // Algolia v5+ removed initIndex; use searchSingleIndex instead
    if (typeof client.initIndex !== 'function') {
      return {
        search(queryOrOpts, opts) {
          const searchParams =
            typeof queryOrOpts === 'string'
              ? { query: queryOrOpts, ...opts }
              : { query: '', ...queryOrOpts };
          return client.searchSingleIndex({
            indexName,
            searchParams: {
              ...getAlgoliaAnalyticsParams(),
              ...searchParams,
            },
          });
        },
        browseObjects: () => Promise.resolve({ hits: [], nbHits: 0 }),
      };
    }
    return client.initIndex(indexName);
  },
};

const emptySearchResults = requests =>
  Promise.resolve({
    results: (Array.isArray(requests) ? requests : []).map(() => ({
      query: '',
      hits: [],
      nbHits: 0,
      nbPages: 0,
      page: 0,
      processingTimeMS: 0,
      hitsPerPage: 0,
      exhaustiveNbHits: true,
      params: '',
      __isArtificial: true,
    })),
  });

const requestParam = (request, key) => {
  if (request == null || typeof request !== 'object') {
    return undefined;
  }
  if (request[key] != null) {
    return request[key];
  }
  if (request.params && typeof request.params === 'object') {
    return request.params[key];
  }
  return undefined;
};

const hasRequestParamValue = value => {
  if (value == null || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  // facets: '*' counts as configured.
  return true;
};

const hasAnalyticsTags = request =>
  hasRequestParamValue(requestParam(request, 'analyticsTags'));

const hasRuleContexts = request =>
  hasRequestParamValue(requestParam(request, 'ruleContexts'));

// Skip InstantSearch empty-mount bootstrap (single products request, no query/filters/facets).
const isBareInstantSearchBootstrap = requests => {
  if (!Array.isArray(requests) || requests.length !== 1) {
    return false;
  }

  const request = requests[0];
  if (request?.indexName !== ALGOLIA_INDEX_NAME.PRODUCTS) {
    return false;
  }

  const query = requestParam(request, 'query');
  if (query) {
    return false;
  }

  return !(
    hasRequestParamValue(requestParam(request, 'filters')) ||
    hasRequestParamValue(requestParam(request, 'numericFilters')) ||
    hasRequestParamValue(requestParam(request, 'facetFilters')) ||
    hasRequestParamValue(requestParam(request, 'facets')) ||
    hasRequestParamValue(requestParam(request, 'attributesToRetrieve'))
  );
};

const injectSearchAnalytics = (requests, userToken) => {
  if (!Array.isArray(requests)) return requests;

  // Page/device/host context for the Algolia dashboard and for context-scoped
  // Rules (CON-8651). Resolved once per batch, and null during SSR — a
  // server-rendered search carries these as explicit `<Configure>` params
  // instead, which is why anything already on the request wins below.
  const ambientParams = getAlgoliaAnalyticsParams();

  return requests.map(request => {
    const analyticsParams = { clickAnalytics: true };
    if (userToken) analyticsParams.userToken = userToken;

    if (ambientParams?.analyticsTags && !hasAnalyticsTags(request)) {
      analyticsParams.analyticsTags = ambientParams.analyticsTags;
    }
    if (ambientParams?.ruleContexts && !hasRuleContexts(request)) {
      analyticsParams.ruleContexts = ambientParams.ruleContexts;
    }

    if (request?.params && typeof request.params === 'object') {
      return {
        ...request,
        params: {
          ...request.params,
          ...analyticsParams,
        },
      };
    }

    return {
      ...request,
      ...analyticsParams,
    };
  });
};

const searchWithClient = (requests, userToken = '') => {
  const client = getAlgoliaClient();
  if (!client) {
    return emptySearchResults(requests);
  }

  const token = userToken || getOrCreateBrowserAlgoliaUserToken();

  return client.search(injectSearchAnalytics(requests, token));
};

export const createSearchClient = userToken => ({
  search(requests) {
    return searchWithClient(requests, userToken);
  },
});

export const searchClient = {
  search(requests) {
    return searchWithClient(requests);
  },
};

// Advanced Search only — skips InstantSearch empty-mount bootstrap.
export const advancedSearchClient = {
  search(requests) {
    const list = Array.isArray(requests)
      ? requests
      : requests && Array.isArray(requests.requests)
      ? requests.requests
      : null;

    if (list && isBareInstantSearchBootstrap(list)) {
      return emptySearchResults(list);
    }

    return searchWithClient(
      Array.isArray(requests) ? requests : list || requests
    );
  },
};

export const noEmptySearchClient = {
  search(requests) {
    if (
      Array.isArray(requests) &&
      requests.every(request => !requestParam(request, 'query'))
    ) {
      return emptySearchResults(requests);
    }

    return searchWithClient(requests);
  },
};
